/**
 * @zendir/ui - PinInput Component
 *
 * Multi-character code entry for passwords, verification codes, and team IDs.
 * Auto-advances focus between cells. Supports numeric and alphanumeric modes.
 *
 * Astro UX Compliance:
 * - Consistent styling with Input component
 * - Focus ring for accessibility
 * - Monospace tabular numbers
 *
 * @example
 * ```tsx
 * <PinInput length={6} type="alphanumeric" value={password} onChange={setPassword} label="Team Password" />
 * <PinInput length={4} type="numeric" value={code} onChange={setCode} label="Verification Code" />
 * <PinInput length={8} value={token} onChange={setToken} label="API Token" />
 * ```
 */

import React, { memo, useState, useRef, useCallback, useEffect } from "react";
import { useTheme } from "../../theme";
import type { LabelPlacement } from "./Input";
import { useBreakpoint } from "../layout/useBreakpoint";

export interface PinInputProps {
  /** Number of character cells (default 6; any positive number, e.g. 4, 6, 8) */
  length?: number;
  /** Input type */
  type?: "numeric" | "alphanumeric";
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Label text or ReactNode (supports rich content like tooltip icons) */
  label?: React.ReactNode;
  /** Helper text */
  helperText?: string;
  /** Error state */
  error?: boolean | string;
  /** Disabled */
  disabled?: boolean;
  /** Mask characters (password mode) */
  mask?: boolean;
  /** Size */
  size?: "small" | "medium" | "large";
  /** Auto-focus first cell on mount */
  autoFocus?: boolean;
  /** Callback when all cells filled */
  onComplete?: (value: string) => void;
  /**
   * Label placement style.
   * - `'outlined'` — label sits on the border of a surrounding container. **Default.**
   * - `'above'`    — label sits above the cells with a gap (classic stack).
   */
  labelPlacement?: LabelPlacement;
  /** Required indicator */
  required?: boolean;
  /** Stretch to full width of container (cells share width equally, same as standard inputs) */
  fullWidth?: boolean;
}

export const PinInput = memo(function PinInput({
  length = 6,
  type = "alphanumeric",
  value,
  onChange,
  label,
  helperText,
  error,
  disabled = false,
  mask = false,
  size = "medium",
  autoFocus = false,
  onComplete,
  labelPlacement = "outlined",
  required,
  fullWidth = false,
}: PinInputProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const { isMobile } = useBreakpoint();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const chars = value.split("").concat(Array(length).fill("")).slice(0, length);

  useEffect(() => {
    if (autoFocus && refs.current[0]) {
      refs.current[0].focus();
    }
  }, [autoFocus]);

  // Between original tokens and the reduced set: slightly more height than 24/32/40, still less than 28/36/44
  const reducedHeight =
    size === "small" ? "26px" : size === "medium" ? "34px" : "42px";
  const controlHeight = isMobile ? "44px" : reducedHeight;
  const sizeConfig = {
    small: { fontSize: tokens.typography.fontSize.xs },
    medium: { fontSize: tokens.typography.fontSize.sm },
    large: { fontSize: tokens.typography.fontSize.base },
  };
  const config = { ...sizeConfig[size], height: controlHeight } as {
    height: string;
    fontSize: string;
  };

  const isValid = (char: string): boolean => {
    if (type === "numeric") return /^[0-9]$/.test(char);
    return /^[a-zA-Z0-9]$/.test(char);
  };

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      // Handle paste (multiple characters). Strip spaces so pasted codes like "12 34 56" work.
      if (inputValue.length > 1) {
        const normalized = inputValue.replace(/\s/g, "");
        const filtered = normalized.split("").filter(isValid).slice(0, length);
        const newValue = filtered.join("");
        onChange(newValue);
        const nextIndex = Math.min(filtered.length, length - 1);
        refs.current[nextIndex]?.focus();
        if (filtered.length === length) onComplete?.(newValue);
        return;
      }

      const char = inputValue;
      if (char && !isValid(char)) return;

      const newChars = [...chars];
      newChars[index] = char.toUpperCase();
      const newValue = newChars.join("").replace(/\s/g, "");
      onChange(newValue);

      // Auto-advance
      if (char && index < length - 1) {
        refs.current[index + 1]?.focus();
      }

      if (newValue.length === length) {
        onComplete?.(newValue);
      }
    },
    [chars, length, onChange, onComplete, type],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === "Backspace") {
        if (!chars[index] && index > 0) {
          refs.current[index - 1]?.focus();
          const newChars = [...chars];
          newChars[index - 1] = "";
          onChange(newChars.join(""));
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        refs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < length - 1) {
        refs.current[index + 1]?.focus();
      }
    },
    [chars, length, onChange],
  );

  const hasError = !!error;
  const errorMessage = typeof error === "string" ? error : undefined;

  const inputBg = isTransparentTheme
    ? tokens.colors.interactive.transparentInputBg || "rgba(139, 92, 246, 0.08)"
    : tokens.colors.background.surface;

  const isOutlined = labelPlacement === "outlined";
  const isFocused = focusedIndex !== null;

  // Background for the outlined label "notch"
  const labelBg = isTransparentTheme
    ? tokens.colors.background.base
    : tokens.colors.background.surface;

  // Outlined label font sizes
  const outlinedLabelSizeMap = {
    small: "0.625rem",
    medium: "0.6875rem",
    large: "0.75rem",
  };
  // Container always uses spacing.sm (8px) horizontal padding, so label left is always 8-6=2px
  const outlinedLabelLeftMap = { small: "2px", medium: "2px", large: "2px" };

  // Shared label content
  const labelContent = (
    <>
      {label}
      {required && (
        <span
          style={{ color: tokens.colors.status.critical, marginLeft: "2px" }}
        >
          *
        </span>
      )}
    </>
  );

  // Cells renderer: fullWidth = cells share space (same total width as standard inputs); otherwise fixed square cells
  const cellsEl = (
    <div
      style={{
        display: "flex",
        gap: tokens.spacing.xs,
        width: fullWidth ? "100%" : undefined,
      }}
    >
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type={mask ? "password" : "text"}
          inputMode={type === "numeric" ? "numeric" : "text"}
          maxLength={1}
          value={chars[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={() => setFocusedIndex(i)}
          onBlur={() => setFocusedIndex(null)}
          onPaste={(e) => {
            e.preventDefault();
            handleChange(i, e.clipboardData.getData("text"));
          }}
          disabled={disabled}
          aria-label={`Character ${i + 1} of ${length}`}
          aria-invalid={hasError || undefined}
          aria-describedby={
            errorMessage
              ? "zendir-pin-error"
              : helperText
                ? "zendir-pin-helper"
                : undefined
          }
          style={{
            ...(fullWidth
              ? { flex: 1, minWidth: 0 }
              : { width: controlHeight }),
            height: config.height,
            textAlign: "center",
            fontSize: config.fontSize,
            fontWeight: tokens.typography.fontWeight.bold,
            fontFamily: tokens.typography.fontFamily.mono,
            fontVariantNumeric: "tabular-nums",
            color: tokens.colors.text.primary,
            backgroundColor: isOutlined ? "transparent" : inputBg,
            border: isOutlined
              ? "none"
              : hasError
                ? `${tokens.borders.width.medium} solid ${tokens.colors.status.critical}`
                : focusedIndex === i
                  ? `${tokens.borders.width.medium} solid ${tokens.colors.accent.primary}`
                  : chars[i]
                    ? `${tokens.borders.width.medium} solid ${tokens.colors.border.default}`
                    : `${tokens.borders.width.medium} solid ${tokens.colors.border.muted}`,
            borderRadius: isOutlined ? 0 : tokens.borderRadius.md,
            outline: "none",
            transition: tokens.animation.fast,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "text",
            ...(!isOutlined && focusedIndex === i
              ? {
                  boxShadow: tokens.borders.focusRing.subtle,
                }
              : {}),
          }}
        />
      ))}
    </div>
  );

  return (
    <div
      style={{
        fontFamily: tokens.typography.fontFamily.primary,
        width: fullWidth ? "100%" : undefined,
      }}
    >
      {/* Label: 'above' variant */}
      {label && !isOutlined && (
        <div
          style={{
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.text.secondary,
            letterSpacing: tokens.typography.letterSpacing.wide,
            marginBottom: tokens.spacing.xs,
          }}
        >
          {labelContent}
        </div>
      )}

      {/* Outlined variant: wrap cells in a bordered container with the label on the border */}
      {isOutlined ? (
        <div
          style={{
            position: "relative",
            width: fullWidth ? "100%" : undefined,
            minHeight: controlHeight,
            border: hasError
              ? tokens.borders.input.error
              : isFocused
                ? tokens.borders.input.focus
                : tokens.borders.input.default,
            borderRadius: tokens.borderRadius.md,
            padding: `0 ${tokens.spacing.sm}`,
            display: "flex",
            alignItems: "center",
            backgroundColor: inputBg,
            transition: tokens.animation.fast,
            boxSizing: "border-box",
            ...(isFocused
              ? {
                  boxShadow: tokens.borders.focusRing.subtle,
                }
              : {}),
          }}
        >
          {label && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: outlinedLabelLeftMap[size],
                transform: "translateY(-50%)",
                backgroundColor: labelBg,
                padding: `0 ${tokens.spacing.xs}`,
                fontSize: outlinedLabelSizeMap[size],
                fontWeight: tokens.typography.fontWeight.medium,
                color: isFocused
                  ? hasError
                    ? tokens.colors.status.critical
                    : tokens.colors.accent.primary
                  : hasError
                    ? tokens.colors.status.critical
                    : tokens.colors.text.secondary,
                letterSpacing: tokens.typography.letterSpacing.wide,
                zIndex: 1,
                pointerEvents: "none",
                transition: tokens.animation.fast,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {labelContent}
            </div>
          )}
          {cellsEl}
        </div>
      ) : (
        cellsEl
      )}

      {(helperText || errorMessage) && (
        <div
          id={errorMessage ? "zendir-pin-error" : "zendir-pin-helper"}
          role={errorMessage ? "alert" : undefined}
          style={{
            fontSize: tokens.typography.fontSize.xs,
            color: hasError
              ? tokens.colors.status.critical
              : tokens.colors.text.tertiary,
            marginTop: tokens.spacing.xs,
          }}
        >
          {errorMessage || helperText}
        </div>
      )}
    </div>
  );
});

export default PinInput;
